import { useState, type ReactNode } from "react";
import { Header, Footer } from '@/components/ui';
import AdminSidebar from '@/components/ui/AdminSidebar';
import { PanelHero } from '@/components/ui/panel';
import { useNavigate } from "react-router-dom";
import { useSettings } from "../context/SettingsContext";
import DeleteIcon from '@/assets/delete.svg';

import userAssociati from "@/assets/user.svg";
import checkmark from "@/assets/checkmark.svg";
import valigia from "@/assets/case.svg";
import database from "@/assets/database.svg";

const inputClass =
  "flex-1 rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5 text-sm text-secondary outline-none transition placeholder:text-gray-400 focus:border-secondary/40 focus:bg-white focus:ring-2 focus:ring-secondary/10";

const addBtnClass =
  "shrink-0 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 hover:shadow-md";

const DANGER_ACTIONS = [
  { tipo: "task", label: "Elimina Task", desc: "Tutte le task audit" },
  { tipo: "contatto", label: "Elimina Leads LinkedIn", desc: "Tutti i contatti LinkedIn" },
  { tipo: "small-lead", label: "Elimina Small Leads", desc: "Tutti gli small leads" },
  { tipo: "startup", label: "Elimina Start-up", desc: "Tutte le start-up" },
] as const;

const DB_TABLES = [
  { name: "Users", color: "from-violet-500/10 to-violet-500/5 text-violet-800" },
  { name: "Leads LinkedIn", color: "from-sky-500/10 to-sky-500/5 text-sky-800" },
  { name: "Small Leads", color: "from-amber-500/10 to-amber-500/5 text-amber-800" },
  { name: "Task audit", color: "from-emerald-500/10 to-emerald-500/5 text-emerald-800" },
];

export default function SettingsPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [confirmDeleteItem, setConfirmDeleteItem] = useState<{ type: string; value: string } | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const navigate = useNavigate();

  const {
    associati, servizi, esiti,
    loading, addItem, deleteItem, deleteDatabaseData
  } = useSettings();

  const [newAssociato, setNewAssociato] = useState("");
  const [newServizio, setNewServizio] = useState("");
  const [newEsito, setNewEsito] = useState("");

  const openModal = (tipo: string) => {
    setModalType(tipo);
    setModalOpen(true);
    setPasswordError(null);
    setMessage(null);
    setPassword("");
  };

  const handleConfirmDelete = async () => {
    if (!modalType) return;
    try {
      setLoadingDelete(true);
      setPasswordError(null);
      const { success, message: msg } = await deleteDatabaseData(modalType, password);

      if (success) {
        setMessage(msg);
        setModalOpen(false);
        setPassword("");
      } else {
        setPasswordError(msg || "Operazione non riuscita.");
      }
    } catch {
      setMessage("Errore durante l'eliminazione");
    } finally {
      setLoadingDelete(false);
    }
  };

  const modalLabels: Record<string, { title: string; body: ReactNode }> = {
    task: { title: "Elimina task audit", body: <>Vuoi eliminare <strong>tutte le task audit</strong> dal database? L'operazione è irreversibile.</> },
    contatto: { title: "Elimina Leads LinkedIn", body: <>Vuoi eliminare <strong>tutti i Leads LinkedIn</strong>? L'operazione è irreversibile.</> },
    "small-lead": { title: "Elimina Small Leads", body: <>Vuoi eliminare <strong>tutti gli Small Leads</strong>? L'operazione è irreversibile.</> },
    startup: { title: "Elimina Start-up", body: <>Vuoi eliminare <strong>tutte le Start-up</strong>? L'operazione è irreversibile.</> },
    tutto: { title: "Eliminazione totale", body: <>Inserisci la password di sicurezza per confermare l'eliminazione completa.</> },
  };

  const renderSection = (
    title: string,
    items: { label: string; value: string }[],
    newValue: string,
    setNewValue: (v: string) => void,
    type: "associati" | "servizi" | "esiti",
    placeholderSingolare: string,
    icon: string
  ) => (
    <div className="flex max-h-[420px] flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/80 px-5 py-4">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary/10">
          <img src={icon} alt="" className="h-5 w-5" />
        </span>
        <h3 className="font-bold text-secondary">{title}</h3>
        <span className="ml-auto rounded-full bg-slate-200/80 px-2 py-0.5 text-xs font-semibold text-secondary/70">
          {items.length}
        </span>
      </div>

      <div className="flex flex-col gap-2 border-b border-slate-100 p-4 xl:flex-row">
        <input
          type="text"
          placeholder={`Nuovo ${placeholderSingolare}`}
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && newValue.trim()) {
              addItem(type, newValue).then(() => setNewValue(""));
            }
          }}
          className={inputClass}
        />
        <button
          type="button"
          onClick={() => addItem(type, newValue).then(() => setNewValue(""))}
          className={addBtnClass}
        >
          Aggiungi
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {items.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-400">Nessun elemento ancora</p>
        ) : (
          <ul className="space-y-2">
            {items.map((item, index) => (
              <li
                key={index}
                className="flex items-center justify-between gap-2 rounded-xl border border-slate-100 bg-slate-50/50 px-3 py-2.5 transition hover:border-slate-200 hover:bg-white"
              >
                <span className="truncate text-sm font-medium text-secondary">{item.label}</span>
                {confirmDeleteItem?.value === item.value && confirmDeleteItem?.type === type ? (
                  <button
                    type="button"
                    className="shrink-0 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-red-700"
                    onClick={() => {
                      deleteItem(type, item.value);
                      setConfirmDeleteItem(null);
                    }}
                  >
                    Conferma
                  </button>
                ) : (
                  <button
                    type="button"
                    className="shrink-0 rounded-lg p-1.5 opacity-60 transition hover:bg-red-50 hover:opacity-100"
                    onClick={() => setConfirmDeleteItem({ type, value: item.value })}
                    aria-label={`Elimina ${item.label}`}
                  >
                    <img src={DeleteIcon} alt="" className="h-4 w-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  return (
    <>
      <Header onAdminClick={() => setSidebarOpen(true)} />
      <AdminSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-12">
        <div className="mx-auto max-w-6xl px-4 py-6 md:px-6">
          <button
            type="button"
            onClick={() => navigate('/home')}
            className="mb-5 inline-flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-semibold text-primary transition hover:bg-primary/5"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Torna alla Home
          </button>

          <PanelHero
            eyebrow="Configurazione"
            title="Impostazioni generali"
            description="Gestisci associati, servizi ed esiti usati nei contatti. La sezione database è riservata alle operazioni critiche."
          />

          {loading ? (
            <div className="mt-10 flex items-center justify-center gap-3 text-gray-500">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-secondary/20 border-t-secondary" />
              Caricamento…
            </div>
          ) : (
            <>
              <div className="mt-8 flex flex-col gap-5 lg:flex-row">
                {renderSection("Associati", associati, newAssociato, setNewAssociato, "associati", "associato", userAssociati)}
                {renderSection("Servizi", servizi, newServizio, setNewServizio, "servizi", "servizio", valigia)}
                {renderSection("Esiti", esiti, newEsito, setNewEsito, "esiti", "esito", checkmark)}
              </div>

              <section className="mt-10 overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
                <div className="flex items-center gap-3 border-b border-slate-100 bg-slate-50/80 px-6 py-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary/10">
                    <img src={database} alt="" className="h-6 w-6" />
                  </span>
                  <div>
                    <h2 className="font-bold text-secondary">Database</h2>
                    <p className="text-xs text-gray-500">Panoramica tabelle e operazioni di manutenzione</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 p-6 sm:grid-cols-4">
                  {DB_TABLES.map((t) => (
                    <div
                      key={t.name}
                      className={`rounded-xl bg-gradient-to-br p-4 text-center ${t.color}`}
                    >
                      <p className="text-sm font-bold">{t.name}</p>
                    </div>
                  ))}
                </div>

                <div className="mx-6 border-t border-slate-100" />

                <div className="m-6 rounded-2xl border border-red-200/80 bg-gradient-to-br from-red-50/80 to-orange-50/30 p-6">
                  <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-100 text-primary">
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </span>
                    <div>
                      <h3 className="font-bold text-primary">Zona pericolosa</h3>
                      <p className="text-xs text-red-800/70">Le eliminazioni sono permanenti e non recuperabili.</p>
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {DANGER_ACTIONS.map((action) => (
                      <button
                        key={action.tipo}
                        type="button"
                        onClick={() => openModal(action.tipo)}
                        className="group rounded-xl border border-red-200/60 bg-white px-4 py-3 text-left transition hover:border-red-300 hover:shadow-sm"
                      >
                        <span className="block text-sm font-semibold text-secondary group-hover:text-primary">
                          {action.label}
                        </span>
                        <span className="mt-0.5 block text-xs text-gray-500">{action.desc}</span>
                      </button>
                    ))}
                  </div>

                  {message && (
                    <p className={`mt-4 rounded-lg px-3 py-2 text-center text-sm font-medium ${
                      message.includes("Errore") || message.includes("❌")
                        ? "bg-red-100 text-red-800"
                        : "bg-emerald-50 text-emerald-800"
                    }`}>
                      {message}
                    </p>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      {modalOpen && modalType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1e2d4a]/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-white/20 bg-white p-6 shadow-2xl">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-primary">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>

            <h2 className="text-center text-xl font-bold text-secondary">
              {modalLabels[modalType]?.title ?? "Conferma eliminazione"}
            </h2>
            <p className="mt-3 text-center text-sm text-gray-600">
              {modalLabels[modalType]?.body}
            </p>

            {modalType === "tutto" && (
              <div className="mt-5">
                <label className="mb-1.5 block text-sm font-medium text-gray-600">
                  Password amministratore
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputClass} w-full ${passwordError ? "border-red-400 focus:ring-red-100" : ""}`}
                  placeholder="Password"
                />
                {passwordError && (
                  <p className="mt-1.5 text-sm font-medium text-red-600">{passwordError}</p>
                )}
              </div>
            )}

            {modalType !== "tutto" && passwordError && (
              <p className="mt-3 text-center text-sm font-medium text-red-600">{passwordError}</p>
            )}

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-semibold text-secondary transition hover:bg-slate-50"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={loadingDelete || (modalType === "tutto" && !password)}
                className="flex-1 rounded-xl bg-red-600 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loadingDelete ? "Eliminazione…" : "Conferma"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}
